import { EmptySearchPageError } from '../common/errors/EmptySearchPageError.js'
import { contactsExportType } from '../const/contactsExportType.js'
import { ExportMetadataFactory } from '../export/exportMetadataFactory.js'
import { utils } from '../common/utils.js'
import { getContactsFromGroupPage } from '../linkedInExportApi.js'
import type {
	ContactsIteratorResult,
	ExportMetadataItem,
	IBroker
} from './types.js'

interface SearchRequest {
	start: number
	pageSize: number
	groupId: string
}

export class LinkedInGroupContactsIterator {
	readonly #broker: IBroker
	#canNext = true
	readonly #searchRequest: SearchRequest
	#total: number | undefined
	readonly #pageSize = 10
	readonly #searchPageUri: URL
	readonly #typeaheadQuery: string
	readonly #metadata: ExportMetadataItem[]

	constructor(broker: IBroker, uri: URL, typeaheadQuery: string) {
		this.#broker = broker
		this.#searchPageUri = uri
		this.#metadata = ExportMetadataFactory.create(uri)
		this.#searchRequest = {
			start: 0,
			pageSize: 10,
			groupId: this.#parseGroupId(uri)
		}
		this.#typeaheadQuery = typeaheadQuery
			? utils.customEncodeUri(typeaheadQuery)
			: '""'
	}

	async getNext(metadata?: object): Promise<ContactsIteratorResult> {
		const response = (await getContactsFromGroupPage(
			this.#broker,
			this.#searchRequest,
			this.#typeaheadQuery,
			metadata
		)) as {
			paging: { total: number; start: number; count: number }
			contacts: unknown[]
		}

		this.#canNext =
			this.#getLimitedTotal(response) >
			response.paging.start + response.paging.count
		if (this.#canNext) {
			this.#searchRequest.start += this.#pageSize
		}

		if (this.#total === undefined) {
			if (response.paging.total === 0) {
				throw new EmptySearchPageError(
					`there is no contacts on the search page ${this.#searchPageUri}`,
					metadata
				)
			}
			this.#total = this.#getLimitedTotal(response)
		}

		const result = {
			found: response.contacts,
			processed:
				response.paging.total > response.paging.start + response.paging.count
					? response.paging.count
					: response.paging.total - response.paging.start
		}
		return result
	}

	canMove(): boolean {
		return this.#canNext
	}

	getTotal(): number {
		return this.#total ?? 0
	}

	getMetadata(): ExportMetadataItem[] {
		return this.#metadata
	}

	getType(): string {
		return contactsExportType.linkedInGroupContactsPage
	}

	#getLimitedTotal(response: { paging: { total: number } }): number {
		if (response.paging.total > 1000) {
			return 1000
		}

		return response.paging.total
	}

	#parseGroupId(uri: URL): string {
		const url = new URL(uri)
		const result = url.pathname.split('/')[2]
		return result
	}
}
